import {
  AccessAttributeValueWithAttribute,
  ResourceId
} from 'src/api/deinverband-api';
import { AccessFilters } from 'src/types/access-filter';
import {
  AttributeMatrix,
  AttributeMatrixProperty,
  StaticAttributeName,
  StaticAttributeValueByName,
  staticAttributeSchemas
} from 'src/types/attributes';
import { Capability } from 'src/types/capabilities';
import { Id } from 'src/types/common';
import { Attendance, Occasion } from 'src/types/occasion';
import { Participation, Survey } from 'src/types/survey';
import { MemberOrId } from 'src/types/user';

/**
 * Build an attribute matrix from a static attribute name and typed value
 */
export const buildStaticAttribute = <K extends StaticAttributeName>(
  name: K,
  value: StaticAttributeValueByName<K>,
  id = 0
): AttributeMatrix => {
  const schema = staticAttributeSchemas[name];

  const properties: AttributeMatrixProperty[] = Object.entries(schema).map(
    ([key, type]) => ({
      type,
      path: `$.${key}`,
      value: (() => {
        const v = (value as Record<string, unknown>)[key];
        return Array.isArray(v) ? v : [v as ResourceId | string | null];
      })()
    })
  );

  return { format: 'matrix', attributeType: name, properties, id };
};

export const buildAdminAttribute = (id = 0): AttributeMatrix =>
  buildStaticAttribute('Admin', {}, id);

export const buildEditorAttribute = (editorId: Id, id = 0): AttributeMatrix =>
  buildStaticAttribute('Editor', { editorId }, id);

export const buildViewerAttribute = (viewerId: Id, id = 0): AttributeMatrix =>
  buildStaticAttribute('Viewer', { viewerId }, id);

export const buildCustomAccessAttribute = (
  userId: Id,
  id = 0
): AttributeMatrix => buildStaticAttribute('CustomAccess', { userId }, id);

export const buildCustomDenyAttribute = (userId: Id, id = 0): AttributeMatrix =>
  buildStaticAttribute('CustomDeny', { userId }, id);

export const buildAttendeesAccess = (
  occasion: Occasion,
  attendances: Attendance[],
  extra: AttributeMatrix[] = []
): AccessFilters => ({
  variant: 'static',
  filters: [
    buildStaticAttribute('AttendeeStatus', {
      eventId: occasion.id,
      statuses: attendances
    }),
    ...extra
  ]
});

export const buildParticipantsAccess = (
  survey: Survey,
  participations: Participation[],
  extra: AttributeMatrix[] = []
): AccessFilters => ({
  variant: 'static',
  filters: [
    buildStaticAttribute('SurveyParticipantStatus', {
      surveyId: survey.id,
      statuses: participations
    }),
    ...extra
  ]
});

export const buildUserAccess = (
  name: StaticAttributeName,
  propertyPath: string,
  members: MemberOrId[]
): AttributeMatrix => ({
  format: 'matrix',
  attributeType: name,
  properties: [
    {
      type: 'user',
      path: propertyPath,
      value: members.map(({ id }) => id)
    }
  ],
  id: 0
});

/**
 * Extract members by role from an attribute matrix
 */
export const extractMembers = (values: AttributeMatrix[]) => {
  const select = (attributeType: StaticAttributeName, propertyPath: string) =>
    (values
      .find((v) => v.attributeType === attributeType)
      ?.properties.find((p) => p.path === propertyPath)?.value || []) as Id[];

  const denied = select('CustomDeny', '$.userId');
  const readers = select('CustomAccess', '$.userId');
  const viewers = select('Viewer', '$.viewerId');
  const editors = select('Editor', '$.editorId');
  const creators = select('Creator', '$.creatorId');

  if (
    denied.length === 0 &&
    readers.length === 0 &&
    viewers.length === 0 &&
    editors.length === 0
  )
    return null;

  return [
    ...readers.map((id) => ({ member: { id }, role: 'reader' as const })),
    ...viewers
      .filter((id) => !creators.includes(id))
      .map((id) => ({ member: { id }, role: 'viewer' as const })),
    ...editors
      .filter((id) => !creators.includes(id))
      .map((id) => ({ member: { id }, role: 'editor' as const })),
    ...denied
      .filter((id) => !creators.includes(id))
      .map((id) => ({ member: { id }, role: 'denied' as const }))
  ];
};

export const injectMembers = (
  values: AttributeMatrix[],
  members: { member: MemberOrId; role: string }[]
): AttributeMatrix[] => {
  // remove the existing member attributes
  const others = values.filter(
    (value) =>
      !['CustomDeny', 'CustomAccess', 'Viewer', 'Editor'].includes(
        value.attributeType
      )
  );

  const valid = members.filter(({ member }) => member.id);
  const select = (role: string) =>
    valid.filter((member) => member.role === role).map(({ member }) => member);

  return [
    ...others,
    buildUserAccess('CustomDeny', '$.userId', select('denied')),
    buildUserAccess('CustomAccess', '$.userId', select('reader')),
    buildUserAccess('Viewer', '$.viewerId', select('viewer')),
    buildUserAccess('Editor', '$.editorId', select('editor'))
  ];
};

export const extractTenantAdmin = (
  values: AccessAttributeValueWithAttribute[]
): boolean => values.some((value) => value.attribute.name === 'Admin');

export const extractEditor = (
  editorId: Id | undefined,
  values: AccessAttributeValueWithAttribute[]
): boolean =>
  values
    .filter((value) => value.attribute.name === 'Editor')
    .map((value) => JSON.parse(value.value) as { editorId: Id })
    .some((value) => value.editorId === editorId);

export const extractCapabilities = (
  values: AccessAttributeValueWithAttribute[]
): Capability[] =>
  values
    .filter((value) => value.attribute.name === 'Capabilities')
    .flatMap(
      (value) =>
        (JSON.parse(value.value) as { capabilities: Capability[] }).capabilities
    );
